import type { Json } from "@/types/supabase.types";
import type { Variable } from "@/domains/templates/types";
import { isJsonPointer } from "@/domains/templates/subanalysis";
import type {
  BoundInputDefinition,
  SubanalysisDefinitionInput,
} from "@/domains/templates/subanalysis.types";

export type SubanalysisTarget = Pick<
  Variable,
  | "variable_id"
  | "variable_name"
  | "variable_name_frontend"
  | "variable_type"
  | "variable_options"
  | "variable_is_required"
  | "variable_default"
>;
export type BindingDraft = {
  target: SubanalysisTarget;
  kind: BoundInputDefinition["binding_kind"];
  path: string;
  sourceVariableId: string;
  constantText: string;
  constantPresent: boolean;
  constantIsNull: boolean;
};
export class SubanalysisFieldError extends Error {
  readonly path: string;
  constructor(path: string, message: string) {
    super(message);
    this.path = path;
  }
}
export type SubanalysisDraft = {
  displayName: string;
  childTemplateId: string;
  sourceStepId: string;
  sourceVariableId: string;
  arrayPath: string;
  bindings: BindingDraft[];
};
export function createBindingDraft(
  target: SubanalysisTarget,
  input?: BoundInputDefinition,
): BindingDraft {
  const value =
    input?.binding_kind === "constant" ? input.variable_default : undefined;
  return {
    target,
    kind:
      input?.binding_kind ??
      (target.variable_is_required && target.variable_default == null
        ? "unbound"
        : "target_default"),
    path:
      input?.binding_kind === "item" || input?.binding_kind === "step_variable"
        ? (input.binding_path ?? "")
        : "",
    sourceVariableId:
      input?.binding_kind === "parent_variable" ||
      input?.binding_kind === "step_variable"
        ? input.binding_source_variable_id
        : "",
    constantText:
      value === undefined || value === null
        ? ""
        : typeof value === "string" &&
            target.variable_type !== "json" &&
            target.variable_type !== "array"
          ? value
          : JSON.stringify(value),
    constantPresent: value !== undefined,
    constantIsNull: value === null,
  };
}
function literal(row: BindingDraft): Json {
  if (!row.constantPresent) throw new Error("Enter a fixed value.");
  if (row.constantIsNull) return null;
  const text = row.constantText;
  switch (row.target.variable_type) {
    case "number":
    case "integer": {
      const value = Number(text);
      if (
        !text.trim() ||
        !Number.isFinite(value) ||
        (row.target.variable_type === "integer" && !Number.isInteger(value))
      )
        throw new Error("Enter a valid number.");
      return value;
    }
    case "boolean":
      if (text !== "true" && text !== "false")
        throw new Error("Choose true or false.");
      return text === "true";
    case "json":
      return JSON.parse(text) as Json;
    case "array": {
      const value: Json = JSON.parse(text);
      if (!Array.isArray(value)) throw new Error("Enter a JSON array.");
      return value;
    }
    default:
      return text;
  }
}
export function toSubanalysisDefinition(
  draft: SubanalysisDraft,
): SubanalysisDefinitionInput {
  if (!draft.childTemplateId)
    throw new SubanalysisFieldError(
      "call.binding_template_id",
      "Select a child template.",
    );
  if (!isJsonPointer(draft.arrayPath))
    throw new SubanalysisFieldError(
      "call.binding_path",
      "Item array must be a JSON Pointer.",
    );
  const targets = new Set<string>();
  const inputs = draft.bindings.map((row, index): BoundInputDefinition => {
    const id = row.target.variable_id;
    if (targets.has(id))
      throw new SubanalysisFieldError(
        `inputs.${index}.binding_target_variable_id`,
        "A child input may only be bound once.",
      );
    targets.add(id);
    const base = { binding_target_variable_id: id };
    try {
      switch (row.kind) {
        case "item":
          if (!isJsonPointer(row.path))
            throw new Error("Enter a valid JSON Pointer.");
          return { ...base, binding_kind: "item", binding_path: row.path };
        case "parent_variable":
          if (!row.sourceVariableId)
            throw new Error("Select a parent variable.");
          return {
            ...base,
            binding_kind: "parent_variable",
            binding_source_variable_id: row.sourceVariableId,
          };
        case "constant":
          return {
            ...base,
            binding_kind: "constant",
            variable_default: literal(row),
          };
        case "target_default":
          return { ...base, binding_kind: "target_default" };
        case "unbound":
          return { ...base, binding_kind: "unbound" };
        case "step_variable":
          if (!row.sourceVariableId)
            throw new SubanalysisFieldError(
              `inputs.${index}.binding_source_variable_id`,
              "Select a source variable.",
            );
          if (!isJsonPointer(row.path))
            throw new SubanalysisFieldError(
              `inputs.${index}.binding_path`,
              "Enter a valid JSON Pointer.",
            );
          return {
            ...base,
            binding_kind: "step_variable",
            binding_source_variable_id: row.sourceVariableId,
            ...(row.path ? { binding_path: row.path } : {}),
          };
      }
    } catch (error) {
      if (error instanceof SubanalysisFieldError) throw error;
      throw new SubanalysisFieldError(
        `inputs.${index}.${row.kind === "item" ? "binding_path" : row.kind === "parent_variable" ? "binding_source_variable_id" : "variable_default"}`,
        error instanceof Error ? error.message : "Invalid binding",
      );
    }
  });
  return {
    call: {
      binding_template_id: draft.childTemplateId,
      binding_source_step_id: draft.sourceStepId || null,
      binding_source_variable_id: draft.sourceVariableId || null,
      binding_path: draft.arrayPath,
    },
    inputs,
  };
}
